import React, { Fragment, useCallback, useEffect, useState } from 'react';
import { always, propEq, sortBy } from 'ramda';
import { Settings } from 'react-slick';

import { Image as ImageType } from '@definitions/artworks.types';
import { renderWhenTrue, renderWhenTrueOtherwise } from '@utils/rendering';
import { SpaceViewStep } from '@hooks/useArtworkSpaceView/useArtworkSpaceView';
import { useBreakpoint } from '@hooks/useBreakpoint';
import useViewportSize from '@hooks/useViewportSize';
import { DESKTOP } from '@theme/media';
import { LazyImage } from '@components/lazyImage';
import { SpaceViewButton } from '@components/PDP/spaceViewButton';
import { Artwork } from '@definitions/artworks.types';

import { ImageDetailView } from '../imageDetailView';

import {
  Container,
  Figure,
  ScrollElement,
  ImageButton,
  MotionImage,
  SpaceViewButtonWrapper,
  SliderWrapper,
  SliderScrollBar,
  Thumb,
  Slider,
  MobileSpaceViewButtonContainer,
} from './artworkImages.styles';

export interface ArtworkImagesProps {
  images: ImageType[];
  urlStandard: string | null;
  isSpaceViewMode: boolean;
  hideImage: boolean;
  spaceViewStep: SpaceViewStep;
  openSpaceViewMode: () => void;
  mobileDetailsHeight?: number;
  artwork: Artwork;
}

const BOTTOM_OFFSET = 10;
const HEADER_HEIGHT = 100;
const SLIDER_NAVIGATION_HEIGHT = 35;
const MIN_IMAGE_HEIGHT = 180;
const BELOW_CAROUSEL_OFFSET = 72;

export const ArtworkImages = ({
  artwork,
  images = [],
  isSpaceViewMode,
  spaceViewStep,
  hideImage,
  openSpaceViewMode,
  urlStandard,
  mobileDetailsHeight,
}: ArtworkImagesProps) => {
  const viewportSize = useViewportSize();
  const sortedImages = sortBy(propEq('isThumbnail', false), images);
  const [carouselOpen, setCarouselOpen] = useState(false);
  const [initialImageIndex, setInitialImageIndex] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [currentSliderIndex, setCurrentSliderIndex] = useState(0);
  const { breakpoint } = useBreakpoint();
  const [availableHeight, setAvailableHeight] = useState(MIN_IMAGE_HEIGHT);
  const [isHeightCalculated, setIsHeightCalculated] = useState(false);

  useEffect(() => {
    if (viewportSize.height && mobileDetailsHeight && !isHeightCalculated) {
      setAvailableHeight(
        viewportSize.height -
          mobileDetailsHeight -
          BOTTOM_OFFSET -
          HEADER_HEIGHT -
          SLIDER_NAVIGATION_HEIGHT -
          BELOW_CAROUSEL_OFFSET
      );
      setIsHeightCalculated(true);
    }
  }, [viewportSize.height]);

  const slickSliderSettings: Settings = {
    arrows: false,
    dots: true,
    beforeChange: (current: number, next: number) => handleBeforeChange(next),
    afterChange: () => handleAfterChange(),
    swipeToSlide: true,
  };

  const handleBeforeChange = useCallback(
    (next: number) => {
      setCurrentSliderIndex(next);
      setIsDragging(true);
    },
    [setIsDragging]
  );

  const handleAfterChange = useCallback(() => {
    setIsDragging(false);
  }, [setIsDragging]);

  const renderCarousel = renderWhenTrue(
    always(
      <ImageDetailView images={sortedImages} setCarouselOpen={setCarouselOpen} initialImageIndex={initialImageIndex} />
    )
  );
  const handleImageClick = (index: number) => {
    setInitialImageIndex(index + 1);
    setCarouselOpen(true);
  };
  const handleSpaceButtonClick = () => {
    openSpaceViewMode();
  };

  const renderImage = ({ urlStandard, urlTiny, description }: ImageType, index: number) =>
    renderWhenTrueOtherwise(
      always(
        <MotionImage
          key={urlStandard}
          src={urlStandard}
          alt={description}
          animate={{
            scale: isSpaceViewMode ? 0.2 : 1,
            x: isSpaceViewMode && breakpoint === DESKTOP ? -100 : 0,
          }}
          transition={{ duration: isSpaceViewMode ? 0.2 : 0, ease: 'linear' }}
          hide={(spaceViewStep === SpaceViewStep.Third && isSpaceViewMode) || hideImage}
        />
      ),
      always(
        <LazyImage key={urlStandard} src={urlStandard} alt={description} placeholderSrc={urlTiny} threshold={-500} />
      )
    )(index === 0);
  const renderSpaceViewButton = renderWhenTrue(
    always(
      <SpaceViewButtonWrapper>
        <SpaceViewButton handleClick={handleSpaceButtonClick} />
      </SpaceViewButtonWrapper>
    )
  );

  const renderScrollbar = renderWhenTrue(() => (
    <SliderScrollBar>
      <Thumb slidersCount={sortedImages.length} index={currentSliderIndex} />
    </SliderScrollBar>
  ));

  return (
    <>
      {/* Desktop version */}
      <Container>
        {renderCarousel(carouselOpen)}
        {sortedImages.map((image, index) => (
          <Fragment key={index}>
            <ScrollElement name={`artwork-image-${index}`}>
              <ImageButton
                onClick={() => handleImageClick(index)}
                isSpaceView={isSpaceViewMode}
                hide={isSpaceViewMode && index !== 0}
              >
                <Figure>{renderImage(image, index)}</Figure>
              </ImageButton>
            </ScrollElement>
            {renderSpaceViewButton(!!urlStandard && (index === 1 || sortedImages.length === 1))}
          </Fragment>
        ))}
      </Container>

      {/* Mobile version */}
      <SliderWrapper>
        {renderCarousel(carouselOpen)}
        <Slider height={availableHeight} {...slickSliderSettings}>
          {sortedImages.map((image, index) => (
            <ImageButton
              key={index}
              onClick={() => !isDragging && handleImageClick(index)}
              isSpaceView={isSpaceViewMode}
              hide={isSpaceViewMode && index !== 0}
            >
              <Figure>{renderImage(image, index)}</Figure>
            </ImageButton>
          ))}
        </Slider>
        {renderScrollbar(sortedImages.length > 1)}
      </SliderWrapper>
      {/* {artwork.category.toLowerCase() != 'products' && (
        <MobileSpaceViewButtonContainer>{renderSpaceViewButton(!!urlStandard)}</MobileSpaceViewButtonContainer>
      )} */}
    </>
  );
};
